'use client'

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Animation, Button, EmptyState, Icon, Loader, Spacer } from '@vinted/web-ui'
import { CrossEmptyState96 } from '@vinted/multichrome-icons'
import classNames from 'classnames'
import { find, isEmpty } from 'lodash'

import { TrackedSellerProductItem } from '@marketplace-web/seller-item-box/seller-item-box-feature'

import { AdShape } from '@marketplace-web/ads/ads-data'
import { AdsContext, Advertisement } from '@marketplace-web/ads/ads-feature'
import {
  IvsGuidelineBanner,
  ListerActivationBanner,
  PromotionalListingBanner,
  PromotionalBanner,
  useBanners,
} from '@marketplace-web/banners/banners-feature'
import { CatalogClosetPromotion, useClosetPromotion } from '@marketplace-web/vas/vas-feature'
import { ClosetModel } from '@marketplace-web/vas/vas-data'
import {
  getFirstListedBreakpoint,
  useBreakpoint,
} from '@marketplace-web/breakpoints/breakpoints-feature'
import { useBrowserNavigation, reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { Grid as ItemGrid } from '@marketplace-web/common-components/grid-ui'
import { useLatestCallback, UiState } from '@marketplace-web/shared/ui-state-util'
import { useAsset } from '@marketplace-web/shared/assets'
import { urlWithParams } from '@marketplace-web/shared/url-util'
import { TrackedProductItem } from '@marketplace-web/item-box/item-box-feature'
import {
  transformCatalogItemDtoToProductItem,
  CatalogItemDto,
  CatalogFrom,
  CatalogFeature,
  catalogInsertPositions,
  CATALOG_PER_PAGE,
  logCatalogError,
  CatalogMvBannerDto,
} from '@marketplace-web/catalog/catalog-data'
import {
  useCatalogContext,
  clickEvent,
  favouriteItemEvent,
  impressionEvent,
} from '@marketplace-web/catalog/catalog-page-data'
import { ErrorBoundary } from '@marketplace-web/error-display/error-display-util'
import { ComponentError } from '@marketplace-web/error-display/error-display-feature'
import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'
import {
  PromotionalBannerModel,
  IvsGuidelineBannerModel,
  ListerActivationBannerModel,
  PromotionalListingBannerInCatalogFeedModel,
  getRandomizedListerActivationBanner,
  dismissBanner,
  useListerActivationBanner,
} from '@marketplace-web/banners/banners-data'

import useItemsCatalogQuery from '../../../hooks/useItemsCatalogQuery'
import {
  adIndexToItem,
  buildGridItems,
  catalogItemDtoToItem,
  closetToItem,
  GenerateGridItem,
  ivsGuidelineBannerToItem,
  listerActivationBannerToItem,
  promotionalListingBannerToItem,
  promotionalBannerToItem,
} from './grid'
import { GridItem } from './types'

const LISTER_ACTIVATION_BREAKPOINT_ITEM_COUNT = {
  phones: 1,
  wide: 4,
  default: 3,
}

type Props = {
  mvBanner?: CatalogMvBannerDto
}

const handleItemsErrorLogging = (err: Error | null) => {
  logCatalogError(err, CatalogFeature.Items)
}

const resolveItemWidth: React.ComponentProps<
  typeof ItemGrid<CatalogItemDto | ClosetModel | Record<string, unknown>>
>['widthResolver'] = item => {
  if (!item.type) return null
  if (
    [
      'closet_promotion',
      'ad',
      'lister_activation_banner',
      'promotional_listing_banner',
      'ivs_guideline_banner',
      'promotional_banner',
    ].includes(item.type)
  )
    return 'full-row'

  return null
}

const Items = ({ mvBanner }: Props) => {
  const asset = useAsset('/assets/animations')
  const breakpoints = useBreakpoint()
  const translate = useTranslate('catalog')
  const { track } = useTracking()
  const { fetchBanners, banners } = useBanners()
  const {
    state: {
      data,
      filters,
      items: { items = [], replicaWarning, error, uiState, searchTrackingParams: searchSession },
    },
  } = useCatalogContext()

  const { page } = useItemsCatalogQuery()
  const {
    catalog_from: catalogFrom,
    search_text: searchQuery,
    search_by_image_uuid: searchByImageUuid,
  } = useBrowserNavigation().searchParams

  const itemOffset = (page - 1) * CATALOG_PER_PAGE

  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchSession
  const catalogUiState = data.uiState
  const searchSessionId = searchSession.sessionId
  const searchCorrelationId = searchSession.correlationId

  const session = useSession()
  const currentUser = session.user

  const isKillswitchBannersEnabled = useFeatureSwitch('killswitch_banners_web')

  const availableListerBannersBySelectedCatalogs = useListerActivationBanner({
    banners,
    selectedCatalogIds: filters.catalogIds,
    catalogs: data.catalogs,
  })

  const { placements } = useContext(AdsContext)
  const adPlacement = find(placements, { shape: AdShape.Inbetween })

  const contentSource = useMemo(() => {
    if (searchQuery) return 'search'
    if (filters.disableSearchSaving) return 'popular_items'

    return 'catalog'
  }, [searchQuery, filters.disableSearchSaving])

  const [showListerActivationBanners, setShowListerActivationBanners] = useState(true)
  const [showPromotionalListingBanner, setShowPromotionalListingBanner] = useState(true)
  const [showIvsGuidelineBanner, setShowIvsGuidelineBanner] = useState(true)

  const seenItemIds = useRef<Array<number>>([])
  const seenAds = useRef<Array<string | number>>([])
  const seenBanners = useRef<Array<string | number>>([])
  const gridBanners = useRef<Array<ReturnType<typeof listerActivationBannerToItem>>>([])
  const { closetsWithBanner, vasEntryPoints } = useClosetPromotion()

  useEffect(() => {
    if (isKillswitchBannersEnabled) return

    fetchBanners()
  }, [isKillswitchBannersEnabled, fetchBanners])

  useEffect(() => {
    gridBanners.current = []
  }, [items])

  const getAdContentSource = () => {
    return contentSource === 'search' ? 'search_ad' : 'catalog_ad'
  }

  const handleAdVisbility = (id: string | number, position: number) => {
    if (seenAds.current.includes(id)) return

    track(
      impressionEvent({
        id,
        position,
        contentType: 'ad',
        contentSource: getAdContentSource(),
        searchSessionId,
        searchCorrelationId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )

    seenAds.current.push(id)
  }

  const handleItemVisibility = (item: CatalogItemDto, position: number) => {
    const { id, user, content_source: itemContentSource } = item
    const { id: itemOwnerId } = user

    if (seenItemIds.current.includes(id)) return

    track(
      impressionEvent({
        id,
        position,
        contentType: 'item',
        contentSource: searchByImageUuid ? 'search_by_image' : itemContentSource,
        itemOwnerId,
        searchCorrelationId,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchScore: item.search_tracking_params?.score,
        searchSignals: item.search_tracking_params?.matched_queries,
        itemDistance: item.search_tracking_params?.buyer_item_distance_km,
      }),
    )

    seenItemIds.current.push(id)
  }

  const handleBannerVisibility = (
    id: string | number,
    banner: ListerActivationBannerModel,
    position: number,
  ) => {
    if (seenBanners.current.includes(id)) return

    track(
      impressionEvent({
        id: banner.catalogId,
        position,
        contentType: 'lister_activation_banner',
        contentSource,
      }),
    )

    seenBanners.current.push(id)
  }

  const handleFavouriteToggle = useCallback(
    (itemContentSource: Parameters<typeof favouriteItemEvent>[0]['contentSource']) =>
      ({ itemId, isFollowEvent }: { itemId: number; isFollowEvent: boolean }) => {
        track(clickEvent({ target: 'favorite' }))
        track(
          favouriteItemEvent({
            itemId,
            isFollowEvent,
            contentSource: itemContentSource,
            globalSearchSessionId,
            globalCatalogBrowseSessionId,
            searchSessionId,
            searchCorrelationId,
          }),
        )
      },
    [
      globalSearchSessionId,
      globalCatalogBrowseSessionId,
      searchCorrelationId,
      searchSessionId,
      track,
    ],
  )

  const handleGridItemVisibility: React.ComponentProps<
    typeof ItemGrid<CatalogItemDto | Record<string, unknown>>
  >['onItemVisible'] = useLatestCallback(gridItem => {
    const {
      item: { data: itemData, type, id },
      index: itemIndex,
    } = gridItem
    const position = itemOffset + itemIndex + 1

    switch (type) {
      case 'item':
        handleItemVisibility(itemData as CatalogItemDto, position)
        break
      case 'lister_activation_banner':
        handleBannerVisibility(id, itemData as ListerActivationBannerModel, position)
        break
      case 'ad':
        handleAdVisbility(id, position)
        break
      default:
        break
    }
  })

  const handleListerActivationBannerDismiss = () => {
    setShowListerActivationBanners(false)

    dismissBanner('lister_activation')
  }

  const handleIvsGuidelineBannerDismiss = () => {
    setShowIvsGuidelineBanner(false)

    dismissBanner('ivs_guideline_banner')
  }

  const handlePromotionalListingBannerDismiss = () => {
    setShowPromotionalListingBanner(false)

    dismissBanner('promotional_listing_banner_in_catalog_feed')
  }

  const generateListerActivationBannerItem: GenerateGridItem<ListerActivationBannerModel> = ({
    itemIndex,
  }) => {
    if (gridBanners.current[itemIndex]) return gridBanners.current[itemIndex]

    const banner = getRandomizedListerActivationBanner(availableListerBannersBySelectedCatalogs)
    const gridBanner = listerActivationBannerToItem(itemIndex, banner)

    gridBanners.current[itemIndex] = gridBanner

    return gridBanner
  }

  const getItemInserts = (): Parameters<typeof buildGridItems>[1] => {
    const itemInserts: Parameters<typeof buildGridItems>[1] = []

    const breakpoint = getFirstListedBreakpoint(breakpoints.active) || 'phones'
    const isAdsBreakpoint = breakpoint === 'phones'

    const closetPromoInsertPositions = catalogInsertPositions.closetPromo[breakpoint]
    const activationBannerInsertPositions = catalogInsertPositions.activationBanner[breakpoint]
    const ivsGuidelineBannerInsertPositions = catalogInsertPositions.ivsGuidelineBanner[breakpoint]
    const adsInsertPositions = catalogInsertPositions.ads[breakpoint]

    if (showListerActivationBanners && availableListerBannersBySelectedCatalogs.length) {
      itemInserts.push({
        items: {
          banners: [],
        },
        positions: activationBannerInsertPositions,
        generateItem: generateListerActivationBannerItem,
      })
    }

    const { promotionalListingBannerInCatalogFeed: promotionalListingBanner } = banners

    if (showPromotionalListingBanner && promotionalListingBanner) {
      itemInserts.push({
        items: {
          banners: [promotionalListingBannerToItem(0, promotionalListingBanner)],
        },
        positions: {
          first: promotionalListingBanner.position.first,
          distance: promotionalListingBanner.position.first,
        },
      })
    }

    const { ivsGuidelineBanner } = banners
    const isIvsBlock = Number(catalogFrom) === CatalogFrom.IvsBlock

    if (showIvsGuidelineBanner && isIvsBlock) {
      itemInserts.push({
        items: {
          banners: [ivsGuidelineBannerToItem(0, ivsGuidelineBanner)],
        },
        positions: ivsGuidelineBannerInsertPositions,
      })
    }

    const { promotionalBanner } = banners

    if (promotionalBanner && promotionalBanner.position.first) {
      itemInserts.push({
        items: {
          banners: [promotionalBannerToItem(0, promotionalBanner)],
        },
        positions: {
          first: promotionalBanner.position.first,
          distance: promotionalBanner.position.first,
        },
      })
    }

    const closetItems = closetsWithBanner.map(closetToItem)

    if (closetItems.length) {
      itemInserts.push({
        items: {
          closet_promotions: closetItems,
        },
        positions: closetPromoInsertPositions,
      })
    }

    if (!mvBanner?.banner && adsInsertPositions && adPlacement && isAdsBreakpoint) {
      const adsItems = Array.from({ length: 4 }, (_, idx) => adIndexToItem(idx))

      itemInserts.push({
        items: {
          ads: adsItems,
        },
        positions: adsInsertPositions,
      })
    }

    return itemInserts
  }

  const renderItem = useCallback(
    (item: CatalogItemDto, position: number) => {
      const url = urlWithParams(item.url, {
        referrer: 'catalog',
      })
      const isViewingSelf = currentUser?.id === item.user.id

      const tracking = {
        id: item.id,
        contentType: 'item',
        contentSource: searchByImageUuid ? 'search_by_image' : item.content_source,
        position,
        searchCorrelationId,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      } as const

      const ProductItemElement = isViewingSelf ? TrackedSellerProductItem : TrackedProductItem

      const productItemProps = {
        item: transformCatalogItemDtoToProductItem(item),
        tracking,
        url,
        ...(isViewingSelf ? {} : { onFavouriteToggle: handleFavouriteToggle(item.content_source) }),
      }

      return <ProductItemElement {...productItemProps} />
    },
    [
      currentUser?.id,
      searchCorrelationId,
      searchSessionId,
      globalSearchSessionId,
      globalCatalogBrowseSessionId,
      handleFavouriteToggle,
      searchByImageUuid,
    ],
  )

  const renderClosetPromotion = useCallback(
    (closet: ClosetModel, position: number) => {
      return (
        <CatalogClosetPromotion
          closet={closet}
          vasEntryPoints={vasEntryPoints}
          position={position}
          contentSource={contentSource}
          searchSessionId={searchSessionId}
          searchCorrelationId={searchCorrelationId}
          globalSearchSessionId={globalSearchSessionId}
          globalCatalogBrowseSessionId={globalCatalogBrowseSessionId}
        />
      )
    },
    [
      contentSource,
      vasEntryPoints,
      searchSessionId,
      searchCorrelationId,
      globalSearchSessionId,
      globalCatalogBrowseSessionId,
    ],
  )

  const renderListerActivationBanner = useCallback(
    (banner: ListerActivationBannerModel) => (
      <ListerActivationBanner
        breakpointItemCount={LISTER_ACTIVATION_BREAKPOINT_ITEM_COUNT}
        banner={banner}
        onClose={handleListerActivationBannerDismiss}
      />
    ),
    [],
  )

  const renderPromotionalListingBanner = useCallback(
    (banner: PromotionalListingBannerInCatalogFeedModel) => (
      <>
        <PromotionalListingBanner banner={banner} onClose={handlePromotionalListingBannerDismiss} />
        <Spacer size={Spacer.Size.Medium} />
      </>
    ),
    [],
  )

  const renderIvsGuidelineBanner = useCallback(
    (banner: IvsGuidelineBannerModel | Record<string, unknown>) => (
      <>
        <IvsGuidelineBanner banner={banner} onClose={handleIvsGuidelineBannerDismiss} />
        <Spacer size={Spacer.Size.Medium} />
      </>
    ),
    [],
  )

  const renderPromotionalBanner = useCallback(
    (banner: PromotionalBannerModel) => (
      <>
        <PromotionalBanner banner={banner} />
        <Spacer size={Spacer.Size.Medium} />
      </>
    ),
    [],
  )

  const getGridItems = () => {
    const gridItems = items.map(catalogItemDtoToItem)

    const itemInserts = getItemInserts()

    return buildGridItems(gridItems, itemInserts)
  }

  const renderGridItem: React.ComponentProps<
    typeof ItemGrid<CatalogItemDto | ClosetModel | Record<string, unknown>>
  >['renderItem'] = useLatestCallback((item, index) => {
    const position = itemOffset + index + 1

    switch (item.type) {
      case 'item':
        return renderItem(item.data as CatalogItemDto, position)
      case 'closet_promotion':
        return renderClosetPromotion(item.data as ClosetModel, position)
      case 'ad':
        return (
          <Advertisement
            isIncremental
            shape={AdShape.Inbetween}
            {...(item.data as Partial<React.ComponentProps<typeof Advertisement>>)}
          />
        )
      case 'lister_activation_banner':
        return renderListerActivationBanner(item.data as ListerActivationBannerModel)
      case 'promotional_listing_banner':
        return renderPromotionalListingBanner(
          item.data as PromotionalListingBannerInCatalogFeedModel,
        )
      case 'ivs_guideline_banner':
        return renderIvsGuidelineBanner(
          item.data as IvsGuidelineBannerModel | Record<string, unknown>,
        )
      case 'promotional_banner':
        return renderPromotionalBanner(item.data as PromotionalBannerModel)
      default:
        return null
    }
  })

  const renderError = (isLoading: boolean) => {
    return (
      <EmptyState
        title={translate('generic_error.title')}
        body={translate('generic_error.body')}
        action={
          <Button
            isLoading={isLoading}
            text={translate('generic_error.cta')}
            styling={Button.Styling.Filled}
            onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
              if (isLoading) {
                event.preventDefault()

                return
              }

              reloadPage()
            }}
          />
        }
      />
    )
  }

  const renderReplica = () => (
    <EmptyState
      icon={<Icon name={CrossEmptyState96} />}
      title={replicaWarning?.title}
      body={replicaWarning?.body}
      testId="search-replica-warning-state"
    />
  )

  const renderEmptyDefault = () => (
    <EmptyState
      animation={<Animation animationUrl={asset('search-empty-state.json')} />}
      title={translate('empty_state.title')}
      body={translate('empty_state.body')}
      testId="search-empty-state"
    />
  )

  const translateSearchByImage = useTranslate('searchbar.search_by_image')

  const renderEmptySearchByImage = () => {
    return (
      <EmptyState
        animation={<Animation animationUrl={asset('search-empty-state.json')} />}
        title={translate('empty_state.title')}
        body={translateSearchByImage('empty.body')}
        testId="search-empty-state"
      />
    )
  }

  const renderEmptyState = () => {
    if (!isEmpty(replicaWarning)) {
      return renderReplica()
    }
    if (items.length === 0 && searchByImageUuid) {
      return renderEmptySearchByImage()
    }
    if (items.length === 0) {
      return renderEmptyDefault()
    }

    return null
  }

  const renderInfoBanner = () => {
    const target = 'merge_catalog_banner'

    return <InfoBanner screen="catalog" linkTracking={{ target }} theme="inverseExperimental" />
  }

  const isError = error || catalogUiState === UiState.Failure
  const isLoading = uiState === UiState.Pending || catalogUiState === UiState.Pending
  const isLoadingWithNoItems =
    catalogUiState === UiState.Success && uiState === UiState.Pending && items.length === 0

  if (isError) return renderError(isLoading)
  if (isLoadingWithNoItems) {
    return (
      <div className="u-flexbox u-justify-content-center">
        <Loader testId="catalog-page-items-loader" />
      </div>
    )
  }

  const loaderClass = classNames({
    'catalog-items__loading': isLoading,
  })

  const gridItems = getGridItems()

  const renderBottomPromotionalListingBanner = () => {
    const bannerGridItem = gridItems.find(item => item.type === 'promotional_listing_banner')
    const banner = banners.promotionalListingBannerInCatalogFeed

    if (!items.length) return null
    if (!showPromotionalListingBanner) return null
    if (!banner) return null
    if (bannerGridItem) return null

    return (
      <>
        {renderPromotionalListingBanner(banner)}
        <Spacer size={Spacer.Size.Medium} />
      </>
    )
  }

  const renderBottomIvsGuidelineBanner = () => {
    const isIvsBlock = Number(catalogFrom) === CatalogFrom.IvsBlock
    const bannerGridItem = gridItems.find(item => item.type === 'ivs_guideline_banner')
    const banner = banners.ivsGuidelineBanner || {}

    if (!items.length) return null
    if (!isIvsBlock) return null
    if (!showIvsGuidelineBanner) return null
    if (bannerGridItem) return null

    return (
      <>
        {renderIvsGuidelineBanner(banner)}
        <Spacer size={Spacer.Size.Medium} />
      </>
    )
  }

  const renderGridItemWithErrorBoundary = (
    item: GridItem<CatalogItemDto | Record<string, unknown>>,
    index: number,
  ) => {
    return (
      <ErrorBoundary
        FallbackComponent={ComponentError}
        onError={handleItemsErrorLogging}
        preventLog
      >
        {renderGridItem(item, index)}
      </ErrorBoundary>
    )
  }

  const renderBottomPromotionalBanner = () => {
    const bannerGridItem = gridItems.find(item => item.type === 'promotional_banner')
    const banner = banners.promotionalBanner

    if (!items.length) return null
    if (!banner) return null
    if (bannerGridItem) return null

    return (
      <>
        {renderPromotionalBanner(banner)}
        <Spacer size={Spacer.Size.Medium} />
      </>
    )
  }

  return (
    <div className={loaderClass}>
      {renderInfoBanner()}
      {renderEmptyState()}

      <ItemGrid
        items={gridItems}
        renderItem={renderGridItemWithErrorBoundary}
        onItemVisible={handleGridItemVisibility}
        widthResolver={resolveItemWidth}
      />

      {renderBottomPromotionalListingBanner()}
      {renderBottomIvsGuidelineBanner()}
      {renderBottomPromotionalBanner()}
    </div>
  )
}

export default Items
